import { useState, useEffect, useMemo } from 'react';
import {
  Wallet,
  Receipt,
  TrendingUp,
  TrendingDown,
  CreditCard,
} from 'lucide-react';
import { PieChart as RechartsChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import {
  StatCardWithComparison,
  CategoryBreakdownChart,
  MonthlyTrendChart,
  PersonalRecentTransactionsList,
  SavingsRateCard,
  RecurringForecastCard,
  TopCategoriesCard,
  WidgetGrid,
  WidgetWrapper,
  BudgetProgressWidget,
  AccountCarousel,
  BalanceHero,
  FlowChart,
  CategoryList,
} from './index';
import PersonalPartnerDetailsWidget from './PersonalPartnerDetailsWidget';
import type { PersonalBudgetSummary, PersonalDashboardStats, PersonalWidgetId, Category, SavingsRate } from '../../types';
import { PERSONAL_WIDGETS } from '../../types';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useThemeStore } from '../../store/themeStore';

interface PersonalDashboardSectionProps {
  summary: PersonalBudgetSummary | null;
  stats: PersonalDashboardStats | null;
  formatCurrency: (amount: number) => string;
  isAnnualView?: boolean;
  isWeeklyView?: boolean;
  visibleWidgets?: PersonalWidgetId[];
  // Paramètres de période pour le drill-down
  month?: number;
  year?: number;
  weekStart?: string;
  weekEnd?: string;
  // Catégories et comptes pour le widget budget
  expenseCategories?: Category[];
  bankAccounts?: { id: string; name: string; color: string }[];
  // Noms des partenaires pour le widget "Qui doit quoi"
  partner1Name?: string;
  partner2Name?: string;
  isCurrentUserPartner1?: boolean;
}

// Tous les widgets par défaut (si pas de config spécifiée)
const ALL_WIDGETS: PersonalWidgetId[] = [
  'totalIncome',
  'totalExpenses',
  'balance',
  'savingsRate',
  'topCategories',
  'recurringForecast',
  'budgetProgress',
  'monthlyTrend',
  'categoryBreakdown',
  'recentTransactions',
  'accountDistribution',
  'accountDetails',
  'personalPartnerDetails',
];

// Helper pour obtenir la taille d'un widget
const getSize = (widgetId: PersonalWidgetId) => {
  const widget = PERSONAL_WIDGETS.find(w => w.id === widgetId);
  return widget?.size || 'small';
};

// Widgets qui font partie du bloc stat cards groupé
const STAT_CARD_WIDGETS: PersonalWidgetId[] = ['totalIncome', 'totalExpenses', 'balance'];

// Calcul du taux d'épargne côté frontend (réplique la logique backend)
function calculateSavingsRate(totalIncome: number, totalExpenses: number): SavingsRate {
  if (totalIncome === 0) {
    return { rate: 0, amount: 0, status: 'average' };
  }
  const savingsAmount = totalIncome - totalExpenses;
  const rate = (savingsAmount / totalIncome) * 100;

  let status: SavingsRate['status'];
  if (rate >= 20) status = 'excellent';
  else if (rate >= 10) status = 'good';
  else if (rate >= 0) status = 'average';
  else if (rate >= -10) status = 'low';
  else status = 'negative';

  return { rate: Math.round(rate * 10) / 10, amount: Math.round(savingsAmount * 100) / 100, status };
}

export default function PersonalDashboardSection({
  summary,
  stats,
  formatCurrency,
  isAnnualView = false,
  isWeeklyView = false,
  visibleWidgets = ALL_WIDGETS,
  month,
  year,
  weekStart,
  weekEnd,
  expenseCategories = [],
  bankAccounts = [],
  partner1Name = 'Partenaire 1',
  partner2Name = 'Partenaire 2',
  isCurrentUserPartner1 = true,
}: PersonalDashboardSectionProps) {
  const { t } = useTranslation('dashboard');
  // Helper pour vérifier si un widget est visible
  const isVisible = (widgetId: PersonalWidgetId) => visibleWidgets.includes(widgetId);

  // Liste des comptes pour le carousel, filtrée par les comptes accessibles (prop)
  const accessibleIds = useMemo(() => new Set(bankAccounts.map(a => a.id)), [bankAccounts]);
  const accounts = useMemo(() => {
    const allAccounts = summary?.accounts?.map(a => ({ id: a.id, name: a.name, color: a.color })) || [];
    if (bankAccounts.length > 0 && summary?.accounts) {
      return allAccounts.filter(a => accessibleIds.has(a.id));
    }
    return allAccounts;
  }, [summary?.accounts, bankAccounts, accessibleIds]);

  // Afficher l'option "Tous les comptes" uniquement si plus d'un compte accessible
  const canShowAll = accounts.length > 1;
  const defaultIndex = canShowAll ? -1 : 0;

  // State carousel : index partagé entre stat cards + savingsRate
  const [statCardsAccountIndex, setStatCardsAccountIndex] = useState(defaultIndex);
  // State carousel : widgets individuels
  const [categoryAccountIndex, setCategoryAccountIndex] = useState(defaultIndex);
  const [transactionsAccountIndex, setTransactionsAccountIndex] = useState(defaultIndex);
  const [accountDetailsIndex, setAccountDetailsIndex] = useState(0);
  const [budgetAccountIndex, setBudgetAccountIndex] = useState(defaultIndex);
  const [trendAccountIndex, setTrendAccountIndex] = useState(defaultIndex);

  // Reset des index quand les données changent (changement de période)
  useEffect(() => {
    setStatCardsAccountIndex(defaultIndex);
    setCategoryAccountIndex(defaultIndex);
    setTransactionsAccountIndex(defaultIndex);
    setAccountDetailsIndex(0);
    setBudgetAccountIndex(defaultIndex);
    setTrendAccountIndex(defaultIndex);
  }, [summary, stats, defaultIndex]);

  // Lookup par ID dans le summary backend (au lieu d'index direct)
  const findSummaryAccount = (index: number) => {
    if (index < 0 || index >= accounts.length) return null;
    const accountId = accounts[index].id;
    return summary?.accounts?.find(a => a.id === accountId) || null;
  };

  // Données pour le camembert des dépenses par compte (filtrées)
  const expenseChartData = summary?.accounts
    .filter(account => account.totalExpenses > 0 && accessibleIds.has(account.id))
    .map(account => ({
      name: account.name,
      value: account.totalExpenses,
      color: account.color,
    })) || [];

  const totalExpenses = summary?.totalExpenses || 0;

  // Données stat cards selon l'index sélectionné
  const statAccount = statCardsAccountIndex >= 0 ? findSummaryAccount(statCardsAccountIndex) : null;
  const statIncome = statAccount ? statAccount.totalIncome : (summary?.totalIncome || 0);
  const statExpenses = statAccount ? statAccount.totalExpenses : (summary?.totalExpenses || 0);
  const statBalance = statAccount
    ? (statAccount.endingBalance ?? statAccount.currentBalance)
    : (summary?.endingBalance ?? summary?.balance ?? 0);
  const isPerAccount = statCardsAccountIndex >= 0;

  const hasMultipleAccounts = accounts.length > 1;

  // Comparaison mois précédent per-account (dérivée de monthlyTrendByAccount)
  const perAccountComparison = useMemo(() => {
    if (statCardsAccountIndex < 0 || !stats) return null;
    const accountId = accounts[statCardsAccountIndex]?.id;
    const accountTrend = stats.monthlyTrendByAccount?.find(a => a.accountId === accountId);
    if (!accountTrend || accountTrend.trends.length < 2) return null;
    const current = accountTrend.trends[accountTrend.trends.length - 1];
    const previous = accountTrend.trends[accountTrend.trends.length - 2];
    const calc = (cur: number, prev: number) => ({
      change: cur - prev,
      changePercent: prev !== 0 ? ((cur - prev) / Math.abs(prev)) * 100 : (cur !== 0 ? 100 : 0),
    });
    return {
      income: calc(current.totalIncome, previous.totalIncome),
      expenses: calc(current.totalExpenses, previous.totalExpenses),
      balance: calc(current.totalIncome - current.totalExpenses, previous.totalIncome - previous.totalExpenses),
    };
  }, [statCardsAccountIndex, stats, accounts]);

  // Helpers pour les comparaisons (global ou per-account)
  const incomeChange = isPerAccount ? perAccountComparison?.income.change : stats?.monthComparison.income.change;
  const incomeChangePercent = isPerAccount ? perAccountComparison?.income.changePercent : stats?.monthComparison.income.changePercent;
  const expensesChange = isPerAccount ? perAccountComparison?.expenses.change : stats?.monthComparison.expenses.change;
  const expensesChangePercent = isPerAccount ? perAccountComparison?.expenses.changePercent : stats?.monthComparison.expenses.changePercent;
  const balanceChange = isPerAccount ? perAccountComparison?.balance.change : stats?.monthComparison.balance.change;
  const balanceChangePercent = isPerAccount ? perAccountComparison?.balance.changePercent : stats?.monthComparison.balance.changePercent;

  // Taux d'épargne selon l'index sélectionné
  const currentSavingsRate = useMemo(() => {
    if (!isPerAccount && stats) return stats.savingsRate;
    if (statAccount) return calculateSavingsRate(statAccount.totalIncome, statAccount.totalExpenses);
    return stats?.savingsRate || { rate: 0, amount: 0, status: 'average' as const };
  }, [isPerAccount, statAccount, stats]);

  // Données categoryBreakdown selon l'index
  const currentCategoryData = useMemo(() => {
    if (categoryAccountIndex === -1 || !stats) return stats?.categoryBreakdown || [];
    const accountData = stats.categoryBreakdownByAccount?.find(
      a => a.accountId === accounts[categoryAccountIndex]?.id
    );
    return accountData?.categories || [];
  }, [categoryAccountIndex, stats, accounts]);

  // Données recentTransactions filtrées
  const currentTransactions = useMemo(() => {
    if (transactionsAccountIndex === -1 || !stats) return stats?.recentTransactions || [];
    const accountName = accounts[transactionsAccountIndex]?.name;
    return stats.recentTransactions.filter(t => t.bankAccountName === accountName);
  }, [transactionsAccountIndex, stats, accounts]);

  // Données monthlyTrend selon l'index
  const currentTrendData = useMemo(() => {
    if (trendAccountIndex === -1 || !stats) return stats?.monthlyTrend || [];
    const accountData = stats.monthlyTrendByAccount?.find(
      a => a.accountId === accounts[trendAccountIndex]?.id
    );
    return accountData?.trends || [];
  }, [trendAccountIndex, stats, accounts]);

  // ─── Hero « Méridien » (miroir du compte commun, sans l'équilibre) ───
  const themeId = useThemeStore((s) => s.themeId);
  const isMeridian = themeId === 'meridian';

  // Séries des micro-courbes, dérivées de la tendance du compte sélectionné.
  const kpiSeries = useMemo(() => {
    const credits = currentTrendData.map((m) => m.totalIncome);
    const debits = currentTrendData.map((m) => m.totalExpenses);
    const savings = currentTrendData.map((m) => (m.totalIncome > 0 ? ((m.totalIncome - m.totalExpenses) / m.totalIncome) * 100 : 0));
    const labels = currentTrendData.map((m) => m.label);
    return { credits, debits, savings, labels };
  }, [currentTrendData]);

  const flowData = useMemo(
    () => currentTrendData.map((m) => ({ label: m.label, income: m.totalIncome, expenses: m.totalExpenses, balance: m.balance })),
    [currentTrendData],
  );

  // Widgets stat cards visibles (pour le bloc groupé)
  const visibleStatCards = STAT_CARD_WIDGETS.filter(w => isVisible(w));

  // Vérifier s'il y a au moins un widget visible
  const hasAnyWidget = visibleWidgets.length > 0;

  if (!hasAnyWidget) {
    return null;
  }

  // Compte sélectionné pour accountDetails
  const detailAccount = findSummaryAccount(accountDetailsIndex);

  // Rendu d'un stat card individuel (utilisé dans le bloc groupé)
  const renderStatCard = (widgetId: PersonalWidgetId) => {
    switch (widgetId) {
      case 'totalIncome':
        return (
          <StatCardWithComparison
            key={widgetId}
            icon={<Wallet className="w-6 h-6 text-green-600" />}
            iconBgColor="bg-green-100"
            label={isPerAccount ? t('common.income') : t('common.totalIncome')}
            value={formatCurrency(statIncome)}
            change={incomeChange}
            changePercent={incomeChangePercent}
          />
        );
      case 'totalExpenses':
        return (
          <StatCardWithComparison
            key={widgetId}
            icon={<Receipt className="w-6 h-6 text-red-600" />}
            iconBgColor="bg-red-100"
            label={isPerAccount ? t('common.expenses') : t('common.totalExpenses')}
            value={formatCurrency(statExpenses)}
            change={expensesChange}
            changePercent={expensesChangePercent}
            invertColors
          />
        );
      case 'balance':
        return (
          <StatCardWithComparison
            key={widgetId}
            icon={
              statBalance >= 0 ? (
                <TrendingUp className="w-6 h-6 text-green-600" />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-600" />
              )
            }
            iconBgColor={statBalance >= 0 ? 'bg-green-100' : 'bg-red-100'}
            label={t('common.balance')}
            value={formatCurrency(statBalance)}
            valueColor={statBalance >= 0 ? 'text-green-600' : 'text-red-600'}
            change={balanceChange}
            changePercent={balanceChangePercent}
          />
        );
      default:
        return null;
    }
  };

  // ═══ Rendu « Méridien » (miroir du compte commun) ═══
  if (isMeridian) {
    const handleGlobalAccountChange = (index: number) => {
      setStatCardsAccountIndex(index);
      setCategoryAccountIndex(index);
      setTransactionsAccountIndex(index);
      setBudgetAccountIndex(index);
      setTrendAccountIndex(index);
    };

    const accountSelector = hasMultipleAccounts ? (
      <select
        value={statCardsAccountIndex}
        onChange={(e) => handleGlobalAccountChange(Number(e.target.value))}
        aria-label={t('accountCarousel.account')}
        className="chip cursor-pointer"
        style={{ paddingRight: 26 }}
      >
        {canShowAll && <option value={-1}>{t('accountCarousel.allAccounts')}</option>}
        {accounts.map((a, i) => (<option key={a.id} value={i}>{a.name}</option>))}
      </select>
    ) : undefined;

    const accountRows = (summary?.accounts ?? []).filter((a) => accessibleIds.has(a.id));
    const showTrendPanel = isVisible('monthlyTrend') && !!stats && !isWeeklyView && flowData.length >= 2;
    const showCategoryPanel = isVisible('categoryBreakdown') && !!stats;
    const expenseByAccount = expenseChartData.map((a) => ({
      ...a,
      percentage: totalExpenses > 0 ? (a.value / totalExpenses) * 100 : 0,
    }));

    return (
      <div className="flex flex-col gap-[22px]">
        {/* HERO : solde + KPI | mes comptes (pas d'équilibre en budget perso) */}
        <section className="grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-[22px] pt-2">
          <BalanceHero
            label={t('common.balance')}
            balance={statBalance}
            changePercent={balanceChangePercent}
            change={balanceChange}
            previousLabel={stats?.monthlyTrend?.[stats.monthlyTrend.length - 2]?.label}
            credits={statIncome}
            debits={statExpenses}
            savingsRate={currentSavingsRate.rate}
            series={kpiSeries}
            formatCurrency={formatCurrency}
            accountSelector={accountSelector}
          />
          <section className="panel">
            <div className="panel-h"><h3>{t('common.myAccounts')}</h3></div>
            {accountRows.length ? (
              <div className="pb-2">
                {accountRows.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 px-[18px] py-3" style={{ borderTop: '1px solid var(--line)' }}>
                    <span className="opt-ic" style={{ ['--optc']: a.color } as React.CSSProperties}>
                      <CreditCard className="w-[18px] h-[18px]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{a.name}</div>
                      <div className="text-[11.5px]" style={{ color: 'var(--text-3)' }}>
                        +{formatCurrency(a.totalIncome)} · −{formatCurrency(a.totalExpenses)}
                      </div>
                    </div>
                    <div className="num text-[15px] font-[660]" style={{ color: 'var(--text)' }}>
                      {formatCurrency(a.endingBalance ?? a.currentBalance)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-[18px] pb-5 text-sm" style={{ color: 'var(--text-3)' }}>{t('common.noBankAccount')}</p>
            )}
          </section>
        </section>

        {/* MID : flux | catégories en liste */}
        {(showTrendPanel || showCategoryPanel) && (
          <section className={clsx('grid grid-cols-1 gap-[22px]', showTrendPanel && showCategoryPanel && 'lg:grid-cols-[1.55fr_1fr]')}>
            {showTrendPanel && (
              <div className="panel">
                <div className="panel-h">
                  <h3>{t('charts.monthlyTrend')}</h3>
                  <div className="chart-legend">
                    <span className="lg"><i style={{ background: 'var(--pa)' }} />{t('common.income')}</span>
                    <span className="lg"><i style={{ background: 'var(--text-3)' }} />{t('common.totalExpenses')}</span>
                    <span className="lg"><i style={{ background: 'var(--chart-balance)' }} />{t('common.balance')}</span>
                  </div>
                </div>
                <FlowChart data={flowData} formatCurrency={formatCurrency} />
              </div>
            )}
            {showCategoryPanel && (
              <div className="panel">
                <div className="panel-h">
                  <h3>{t('charts.categoryBreakdown')}</h3>
                  <span className="hint">{t('categoryBreakdownDetails.clickToDrill')}</span>
                </div>
                <CategoryList
                  categories={currentCategoryData}
                  formatCurrency={formatCurrency}
                  month={month}
                  year={year}
                  weekStart={weekStart}
                  weekEnd={weekEnd}
                  isPersonal
                />
              </div>
            )}
          </section>
        )}

        {/* Widgets restants (le panneau « Mes comptes » remplace accountDetails). */}
        <WidgetGrid>
          {isVisible('savingsRate') && stats && (
            <WidgetWrapper size={getSize('savingsRate')}>
              <SavingsRateCard savingsRate={currentSavingsRate} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('topCategories') && stats && (
            <WidgetWrapper size={getSize('topCategories')}>
              <TopCategoriesCard categories={stats.topCategories} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('recurringForecast') && stats && !isAnnualView && !isWeeklyView && (
            <WidgetWrapper size={getSize('recurringForecast')}>
              <RecurringForecastCard forecast={stats.recurringForecast} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('budgetProgress') && (
            <WidgetWrapper size={getSize('budgetProgress')}>
              <BudgetProgressWidget
                type="personal"
                formatCurrency={formatCurrency}
                month={month}
                year={year}
                weekStart={weekStart}
                weekEnd={weekEnd}
                expenseCategories={expenseCategories}
                bankAccounts={bankAccounts}
                filterBankAccountId={budgetAccountIndex >= 0 ? accounts[budgetAccountIndex]?.id : undefined}
              />
            </WidgetWrapper>
          )}
          {isVisible('recentTransactions') && stats && (
            <WidgetWrapper size={getSize('recentTransactions')}>
              <PersonalRecentTransactionsList transactions={currentTransactions} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('accountDistribution') && (
            <WidgetWrapper size={getSize('accountDistribution')}>
              <div className="panel h-full">
                <div className="panel-h"><h3>{t('common.expensesByAccount')}</h3></div>
                {expenseByAccount.length ? (
                  <div className="cats">
                    {expenseByAccount.map((a, i) => (
                      <div className="cat" key={`${a.name}-${i}`}>
                        <div className="c-name"><i style={{ background: a.color }} /><span className="truncate">{a.name}</span></div>
                        <div className="c-amt num">{formatCurrency(a.value)}</div>
                        <div className="c-bar"><span style={{ width: `${Math.min(100, a.percentage)}%`, background: a.color }} /></div>
                        <div className="c-pct num">{Math.round(a.percentage)} %</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="px-[18px] pb-5 text-sm" style={{ color: 'var(--text-3)' }}>{t('common.noExpensesThisPeriod')}</p>
                )}
              </div>
            </WidgetWrapper>
          )}
          {isVisible('personalPartnerDetails') && stats?.partnerBalance && (
            <WidgetWrapper size={getSize('personalPartnerDetails')}>
              <PersonalPartnerDetailsWidget
                partnerBalance={stats.partnerBalance}
                partner1Name={partner1Name}
                partner2Name={partner2Name}
                formatCurrency={formatCurrency}
                isCurrentUserPartner1={isCurrentUserPartner1}
              />
            </WidgetWrapper>
          )}
        </WidgetGrid>
      </div>
    );
  }

  return (
    <WidgetGrid>
      {/* Bloc stat cards groupé (quand multi-comptes) */}
      {hasMultipleAccounts && visibleStatCards.length > 0 && (
        <WidgetWrapper size="large">
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
            <AccountCarousel
              accounts={accounts}
              currentIndex={statCardsAccountIndex}
              onIndexChange={setStatCardsAccountIndex}
              showAllOption={canShowAll}
              compact
            />
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${visibleStatCards.length > 2 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-3 mt-3`}>
              {visibleStatCards.map(renderStatCard)}
            </div>
          </div>
        </WidgetWrapper>
      )}

      {/* Stat cards individuels (quand un seul compte) */}
      {!hasMultipleAccounts && (
        <>
          {isVisible('totalIncome') && (
            <WidgetWrapper size={getSize('totalIncome')}>
              <StatCardWithComparison
                icon={<Wallet className="w-6 h-6 text-green-600" />}
                iconBgColor="bg-green-100"
                label={t('common.totalIncome')}
                value={formatCurrency(statIncome)}
                change={stats?.monthComparison.income.change}
                changePercent={stats?.monthComparison.income.changePercent}
              />
            </WidgetWrapper>
          )}

          {isVisible('totalExpenses') && (
            <WidgetWrapper size={getSize('totalExpenses')}>
              <StatCardWithComparison
                icon={<Receipt className="w-6 h-6 text-red-600" />}
                iconBgColor="bg-red-100"
                label={t('common.totalExpenses')}
                value={formatCurrency(statExpenses)}
                change={stats?.monthComparison.expenses.change}
                changePercent={stats?.monthComparison.expenses.changePercent}
                invertColors
              />
            </WidgetWrapper>
          )}

          {isVisible('balance') && (
            <WidgetWrapper size={getSize('balance')}>
              <StatCardWithComparison
                icon={
                  statBalance >= 0 ? (
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  )
                }
                iconBgColor={statBalance >= 0 ? 'bg-green-100' : 'bg-red-100'}
                label={t('common.balance')}
                value={formatCurrency(statBalance)}
                valueColor={statBalance >= 0 ? 'text-green-600' : 'text-red-600'}
                change={stats?.monthComparison.balance.change}
                changePercent={stats?.monthComparison.balance.changePercent}
              />
            </WidgetWrapper>
          )}
        </>
      )}

      {/* Taux d'épargne */}
      {isVisible('savingsRate') && stats && (
        <WidgetWrapper size={getSize('savingsRate')}>
          <SavingsRateCard
            savingsRate={currentSavingsRate}
            formatCurrency={formatCurrency}
          />
        </WidgetWrapper>
      )}

      {/* Top catégories */}
      {isVisible('topCategories') && stats && (
        <WidgetWrapper size={getSize('topCategories')}>
          <TopCategoriesCard
            categories={stats.topCategories}
            formatCurrency={formatCurrency}
          />
        </WidgetWrapper>
      )}

      {/* Prévisions récurrentes (masqué en vue annuelle et hebdomadaire) */}
      {isVisible('recurringForecast') && stats && !isAnnualView && !isWeeklyView && (
        <WidgetWrapper size={getSize('recurringForecast')}>
          <RecurringForecastCard
            forecast={stats.recurringForecast}
            formatCurrency={formatCurrency}
          />
        </WidgetWrapper>
      )}

      {/* Budgets prévisionnels */}
      {isVisible('budgetProgress') && (
        <WidgetWrapper size={getSize('budgetProgress')}>
          <BudgetProgressWidget
            type="personal"
            formatCurrency={formatCurrency}
            month={month}
            year={year}
            weekStart={weekStart}
            weekEnd={weekEnd}
            expenseCategories={expenseCategories}
            bankAccounts={bankAccounts}
            filterBankAccountId={budgetAccountIndex >= 0 ? accounts[budgetAccountIndex]?.id : undefined}
            carouselElement={
              <AccountCarousel
                accounts={accounts}
                currentIndex={budgetAccountIndex}
                onIndexChange={setBudgetAccountIndex}
                showAllOption={canShowAll}
              />
            }
          />
        </WidgetWrapper>
      )}

      {/* Tendance mensuelle (masqué en vue hebdomadaire) */}
      {isVisible('monthlyTrend') && stats && !isWeeklyView && (
        <WidgetWrapper size={getSize('monthlyTrend')}>
          <div className="card h-full">
            <AccountCarousel
              accounts={accounts}
              currentIndex={trendAccountIndex}
              onIndexChange={setTrendAccountIndex}
              showAllOption={canShowAll}
            />
            <MonthlyTrendChart
              data={currentTrendData}
              formatCurrency={formatCurrency}
              noCard
            />
          </div>
        </WidgetWrapper>
      )}

      {/* Dépenses par catégorie */}
      {isVisible('categoryBreakdown') && stats && (
        <WidgetWrapper size={getSize('categoryBreakdown')}>
          <div className="card h-full">
            <AccountCarousel
              accounts={accounts}
              currentIndex={categoryAccountIndex}
              onIndexChange={setCategoryAccountIndex}
              showAllOption={canShowAll}
            />
            <CategoryBreakdownChart
              data={currentCategoryData}
              formatCurrency={formatCurrency}
              month={month}
              year={year}
              weekStart={weekStart}
              weekEnd={weekEnd}
              isPersonal={true}
              noCard
            />
          </div>
        </WidgetWrapper>
      )}

      {/* Transactions récentes */}
      {isVisible('recentTransactions') && stats && (
        <WidgetWrapper size={getSize('recentTransactions')}>
          <div className="card h-full">
            <AccountCarousel
              accounts={accounts}
              currentIndex={transactionsAccountIndex}
              onIndexChange={setTransactionsAccountIndex}
              showAllOption={canShowAll}
            />
            <PersonalRecentTransactionsList
              transactions={currentTransactions}
              formatCurrency={formatCurrency}
              noCard
            />
          </div>
        </WidgetWrapper>
      )}

      {/* Répartition par compte */}
      {isVisible('accountDistribution') && (
        <WidgetWrapper size={getSize('accountDistribution')}>
          <div className="card h-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('common.expensesByAccount')}
            </h3>
            {expenseChartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsChart>
                    <Pie
                      data={expenseChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => {
                        const percentage = totalExpenses > 0 ? (value / totalExpenses) * 100 : 0;
                        return `${name}: ${percentage.toFixed(1)}%`;
                      }}
                    >
                      {expenseChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                  </RechartsChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                {t('common.noExpensesThisPeriod')}
              </div>
            )}
          </div>
        </WidgetWrapper>
      )}

      {/* Détails par compte bancaire */}
      {isVisible('accountDetails') && (
        <WidgetWrapper size={getSize('accountDetails')}>
          <div className="card h-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('common.accountDetails')}
            </h3>
            <AccountCarousel
              accounts={accounts}
              currentIndex={accountDetailsIndex}
              onIndexChange={setAccountDetailsIndex}
            />
            {detailAccount ? (
              <div className="p-4 rounded-lg border border-gray-100 bg-gray-50 mt-2">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: detailAccount.color }}
                  />
                  <span className="font-semibold text-gray-900">{detailAccount.name}</span>
                  <CreditCard className="w-4 h-4 text-gray-400 ml-auto" />
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('common.income')}</span>
                    <span className="font-medium text-green-600">
                      +{formatCurrency(detailAccount.totalIncome)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('common.expenses')}</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(detailAccount.totalExpenses)}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between">
                    <span className="text-gray-600">{t('common.periodBalance')}</span>
                    <span className={`font-medium ${detailAccount.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {detailAccount.balance >= 0 ? '+' : ''}{formatCurrency(detailAccount.balance)}
                    </span>
                  </div>
                  <div className="flex justify-between bg-gray-100 -mx-4 px-4 py-2 mt-2 rounded-b-lg">
                    <span className="text-gray-700 font-medium">{t('common.balance')}</span>
                    <span className={`font-bold ${(detailAccount.endingBalance ?? detailAccount.currentBalance) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(detailAccount.endingBalance ?? detailAccount.currentBalance)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>{t('common.noBankAccount')}</p>
                  <p className="text-sm">{t('common.addAccountsInSettings')}</p>
                </div>
              </div>
            )}
          </div>
        </WidgetWrapper>
      )}

      {/* Widget "Qui doit quoi" (mode personal_with_transfers uniquement) */}
      {isVisible('personalPartnerDetails') && stats?.partnerBalance && (
        <WidgetWrapper size={getSize('personalPartnerDetails')}>
          <PersonalPartnerDetailsWidget
            partnerBalance={stats.partnerBalance}
            partner1Name={partner1Name}
            partner2Name={partner2Name}
            formatCurrency={formatCurrency}
            isCurrentUserPartner1={isCurrentUserPartner1}
          />
        </WidgetWrapper>
      )}
    </WidgetGrid>
  );
}
